import React, { useState, useContext, useEffect } from 'react'
import { Box, Tabs, Tab, Stack, Button, TextField } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { grayBg } from '@/constant'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { getUserInfo, updateUserName } from '@/api'
import { getSecurityGate, getSecurityProfile } from '@/api/user'
import { AuthContext } from '@/layout/Auth'
import { useBoolean, useRequest } from 'ahooks'
import { CheckCircleFilled } from '@/icon'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import Icon from '@/components/Icon'
import { Card, Text, Row, Text2, TextWhite2, VisuallyHiddenInput } from './common'
import History from '../Account/Notifications/History'
import BindPhoneModal from './BindPhoneModal'
import PasswordModal from './PasswordModal'
import WriteOffModal from './WriteOffModal'
import ModifyPhoneModal from './ModifyPhoneModal'
import BindEmailModal from './BindEmailModal'
import RealNameAuthModal from './RealNameAuthModal'
import CropImageModal from './CropImageModal'
import Promotion from './Promotion'
import BindWechat from '../Account/Basic/bindWechat'
import personBg from '@/asset/img/personal_bg.png'
import defaultAvatar from '@/asset/img/default_avatar.png'
import { Message } from '@cx/ui'

export enum TabValue {
  Base = 'base',
  Message = 'message',
  Promotion = 'promotion',
}

const factorLabelMap: Record<string, string> = {
  phone: '手机号',
  email: '邮箱',
  wechat: '微信',
  password: '密码',
}

const Personal = () => {
  const {
    authInfo: { userInfo, verified, promotionInfo },
    updateAuthInfo,
  } = useContext(AuthContext)
  const { id } = useParams() as { id: TabValue }
  const [value, setValue] = useState<TabValue>(id || TabValue.Base)
  const [image, setImage] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [nickname, setNickname] = useState<string>(userInfo?.nickname || '')
  const [editNicknameStatus, { setTrue: openEditNicknameStatus, setFalse: closeEditNicknameStatus }] = useBoolean(false)
  const [bindPhoneModalVisible, setBindPhoneModalVisible] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [writeOffModalVisible, setWriteOffModalVisible] = useState(false)
  const [modifyPhoneModalVisible, setModifyPhoneModalVisible] = useState(false)
  const [bindEmailModalVisible, setBindEmailModalVisible] = useState(false)
  const [bindWechatModalVisible, setBindWechatModalVisible] = useState(false)
  const [cropImageModalVisible, setCropImageModalVisible] = useState(false)
  const [realNameAuthModalVisible, setRealNameAuthModalVisible] = useState(false)

  const { data: securityProfile, run: loadSecurityProfile } = useRequest(getSecurityProfile, { manual: true })
  const { runAsync: checkSecurityGate } = useRequest((scene: string) => getSecurityGate(scene), { manual: true })
  const hasWechatBound = Boolean(securityProfile?.has_wechat)
  const wechatDisplayText = userInfo?.wechat_nickname || (hasWechatBound ? '已绑定微信' : '-')

  const handleChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setValue(newValue)
    navigate(`/console/personal/${newValue}`)
  }

  const refreshProfile = () => {
    return getUserInfo()
      .then((result) => {
        updateAuthInfo({
          auth: true,
          userInfo: result,
          verified: result?.is_certified === 1,
        })
        loadSecurityProfile()
      })
      .catch(() => {
        updateAuthInfo({
          auth: false,
          userInfo: null,
          verified: false,
          orgInfo: null,
        })
      })
  }

  const openActionWithGate = async (scene: string, onPass: () => void) => {
    try {
      const result = await checkSecurityGate(scene)
      if (result.pass) {
        onPass()
        return
      }
      const missing = (result.missing_factors || []).map((item) => factorLabelMap[item] || item).join('、')
      Message.warning(`账号安全等级较低，请先补充${missing || '邮箱、微信或密码'}后再继续`)
    } catch (error: any) {
      Message.error(error?.message || '安全校验失败，请稍后重试')
    }
  }

  const onUpdateUserName = () => {
    return updateUserName({ nickname }).then(() => {
      Message.success('修改成功')
      refreshProfile()
      closeEditNicknameStatus()
    })
  }

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      setImage(event.target?.result as string)
      setCropImageModalVisible(true)
    }
    reader.readAsDataURL(file)
  }

  useEffect(() => {
    setNickname(userInfo?.nickname || '')
  }, [userInfo])

  useEffect(() => {
    if (!Object.values(TabValue).includes(id)) {
      navigate(`/console/personal/${TabValue.Base}`)
      setValue(TabValue.Base)
    } else {
      setValue(id)
    }
  }, [id, navigate])

  useEffect(() => {
    loadSecurityProfile()
  }, [loadSecurityProfile])

  useEffect(() => {
    const errorMsg = searchParams.get('errorMsg')
    const securityWechatVerified = searchParams.get('securityWechatVerified')
    if (errorMsg) {
      Message.error(errorMsg)
    }
    const securityState = searchParams.get('securityState')
    if (securityWechatVerified === '1' && securityState) {
      const parts = securityState.split(':')
      const scene = parts.length >= 2 ? parts[1] : ''
      if (scene) {
        sessionStorage.setItem(`security-wechat-state:${scene}`, securityState)
      }
      Message.success('微信扫码校验成功，请继续完成当前操作')
    }
    if (errorMsg || securityWechatVerified === '1' || securityState) {
      navigate(`/console/personal/${value}`, { replace: true })
    }
  }, [navigate, searchParams, value])

  const missingFactorText = (securityProfile?.missing_factors || []).map((item) => factorLabelMap[item] || item).join('、')
  const securityDesc =
    securityProfile?.security_level === '高'
      ? '当前账号验证因子较完整，高风险操作可直接通过统一门禁校验。'
      : `你的账号安全等级较低，建议补充${missingFactorText || '邮箱、微信或密码'}。`

  return (
    <Box
      sx={{
        pt: 13,
        backgroundColor: grayBg,
        height: '100vh',
        overflowY: 'auto',
        pb: 5,
      }}
    >
      <CropImageModal
        open={cropImageModalVisible}
        onOk={(img) => {
          updateUserName({ nickname, head_img_url: img })
            .then(() => {
              refreshProfile()
              setCropImageModalVisible(false)
            })
            .catch(() => {
              setCropImageModalVisible(false)
            })
        }}
        onCancel={() => {
          setCropImageModalVisible(false)
        }}
        url={image}
      />
      <BindPhoneModal
        open={bindPhoneModalVisible}
        onOk={refreshProfile}
        onCancel={() => {
          setBindPhoneModalVisible(false)
        }}
      />
      <ModifyPhoneModal
        currentPhone={userInfo?.phone}
        currentMail={userInfo?.mail}
        open={modifyPhoneModalVisible}
        onOk={refreshProfile}
        onCancel={() => {
          setModifyPhoneModalVisible(false)
        }}
      />
      <WriteOffModal
        verified={verified}
        currentPhone={userInfo?.phone}
        currentMail={userInfo?.mail}
        open={writeOffModalVisible}
        onCancel={() => {
          setWriteOffModalVisible(false)
        }}
      />
      <PasswordModal
        hasPassword={securityProfile?.has_password}
        currentPhone={userInfo?.phone}
        currentMail={userInfo?.mail}
        open={passwordModalVisible}
        onOk={refreshProfile}
        onCancel={() => {
          setPasswordModalVisible(false)
        }}
      />
      <BindEmailModal
        currentMail={userInfo?.mail}
        currentPhone={userInfo?.phone}
        open={bindEmailModalVisible}
        onOk={refreshProfile}
        onCancel={() => {
          setBindEmailModalVisible(false)
        }}
      />
      <BindWechat
        open={bindWechatModalVisible}
        onClose={() => {
          setBindWechatModalVisible(false)
          refreshProfile()
        }}
      />
      <RealNameAuthModal
        open={realNameAuthModalVisible}
        onOk={refreshProfile}
        onCancel={() => {
          setRealNameAuthModalVisible(false)
        }}
      />
      <Stack gap={3} sx={{ width: 1120, mx: 'auto', position: 'relative' }}>
        <Box
          sx={{
            top: -24,
            position: 'absolute',
            right: 0,
            color: 'rgba(0,0,0,0.5)',
            cursor: 'pointer',
            fontSize: 12,
            '&:hover': { color: 'primary.main' },
          }}
          onClick={() => {
            navigate(value === TabValue.Base ? '/console/account/profile/basic' : '/console/account/notifications/history')
          }}
        >
          返回旧版
        </Box>
        <Card sx={{ p: 0 }}>
          <Box
            sx={{
              position: 'relative',
              height: 144,
              backgroundSize: '100% auto',
              backgroundImage: `url(${personBg})`,
              borderRadius: '8px 8px 0px 0px',
            }}
          >
            <Stack direction='row' justifyContent='space-between' sx={{ pt: '50px', pl: 5, pr: 3 }}>
              <Stack direction='row' gap={3}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 126,
                    height: 126,
                    borderRadius: '50%',
                    border: '3px solid #fff',
                  }}
                >
                  <img
                    src={userInfo?.head_img_url || defaultAvatar}
                    style={{ width: '100%', height: '100%', display: 'block', borderRadius: '50%' }}
                  />
                  <Box
                    component='label'
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      border: '5px solid #fff',
                      bottom: 0,
                      right: -5,
                      backgroundColor: 'primary.main',
                      cursor: 'pointer',
                    }}
                  >
                    <VisuallyHiddenInput type='file' accept='image/*' onChange={onImageChange} />
                    <Icon type='icon-photo' sx={{ color: '#fff', fontSize: 18 }} />
                  </Box>
                </Box>
                <Box sx={{ color: '#fff', position: 'absolute', bottom: 16, left: 198 }}>
                  <Box sx={{ fontSize: 26, fontWeight: 600 }}>{userInfo?.nickname}</Box>
                  <Stack direction='row' alignItems='center' gap={1}>
                    <Text2> {userInfo?.id}</Text2>
                    <CopyToClipboard
                      text={userInfo?.id?.toString() || ''}
                      onCopy={() => {
                        Message.success('用户ID已复制到剪贴板')
                      }}
                    >
                      <Icon type='icon-copy' sx={{ width: '12px', color: '#fff', cursor: 'pointer' }} />
                    </CopyToClipboard>
                  </Stack>
                </Box>
              </Stack>

              <Stack direction='row' gap={5} sx={{ position: 'absolute', bottom: 16, right: 24 }}>
                <Stack direction='row' gap={1.5} alignItems='baseline'>
                  <Text2>注册时间</Text2>
                  <TextWhite2>{userInfo?.created_at}</TextWhite2>
                </Stack>
                <Stack direction='row' gap={1.5} alignItems='baseline'>
                  <Text2>最后登录时间</Text2>
                  <TextWhite2>{userInfo?.last_logined_at}</TextWhite2>
                </Stack>
              </Stack>
            </Stack>
          </Box>
          <Stack direction='row' gap={2} justifyContent='flex-end' alignItems='center' sx={{ pr: 3 }}>
            <Tabs value={value} onChange={handleChange}>
              <Tab disableRipple value={TabValue.Base} icon={<Icon type='icon-xiaolian' />} iconPosition='start' label='基本信息' />
              <Tab disableRipple value={TabValue.Message} icon={<Icon type='icon-tongzhi' />} iconPosition='start' label='消息中心' />
              {promotionInfo?.referral_code && (
                <Tab
                  disableRipple
                  value={TabValue.Promotion}
                  icon={
                    <Icon
                      sx={{ fontSize: 24, mt: '-8px' }}
                      type={
                        value === TabValue.Promotion
                          ? 'icon-tuiguangdashiicon-xuanzhong'
                          : 'icon-tuiguangdashiicon-weixuanzhong'
                      }
                    />
                  }
                  iconPosition='start'
                  label='推广大使'
                />
              )}
            </Tabs>
          </Stack>
        </Card>
        {value === TabValue.Base && (
          <>
            <Card>
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                <Box>
                  <Text>账号安全</Text>
                  <Text2 sx={{ color: 'rgba(0,0,0,0.5)', mt: 1 }}>{securityDesc}</Text2>
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: 10,
                    fontSize: 14,
                    color:
                      securityProfile?.security_level === '高'
                        ? 'success.main'
                        : securityProfile?.security_level === '中'
                          ? 'warning.main'
                          : 'error.main',
                    backgroundColor:
                      securityProfile?.security_level === '高'
                        ? 'rgba(46, 125, 50, 0.08)'
                        : securityProfile?.security_level === '中'
                          ? 'rgba(237, 108, 2, 0.08)'
                          : 'rgba(211, 47, 47, 0.08)',
                  }}
                >
                  安全等级：{securityProfile?.security_level || '低'}
                </Box>
              </Stack>
              <Row sx={{ borderBottom: '1px solid #EEEEEE' }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>手机号</Text>
                <Text sx={{ width: 590 }}>{userInfo?.phone || '-'}</Text>
                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  <Button
                    size='small'
                    onClick={() => {
                      if (!userInfo?.phone) {
                        setBindPhoneModalVisible(true)
                      } else {
                        openActionWithGate('change_phone', () => setModifyPhoneModalVisible(true))
                      }
                    }}
                  >
                    {userInfo?.phone ? '修改' : '绑定'}
                  </Button>
                </Stack>
              </Row>
              <Row sx={{ borderBottom: '1px solid #EEEEEE' }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>邮箱</Text>
                <Text sx={{ width: 590 }}>{userInfo?.mail || '-'}</Text>
                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  <Button
                    size='small'
                    onClick={() => {
                      if (!userInfo?.mail) {
                        setBindEmailModalVisible(true)
                      } else {
                        openActionWithGate('change_email', () => setBindEmailModalVisible(true))
                      }
                    }}
                  >
                    {userInfo?.mail ? '修改' : '绑定'}
                  </Button>
                </Stack>
              </Row>
              <Row sx={{ borderBottom: '1px solid #EEEEEE' }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>微信</Text>
                <Text sx={{ width: 590 }}>{wechatDisplayText}</Text>
                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  {!hasWechatBound ? (
                    <Button size='small' onClick={() => setBindWechatModalVisible(true)}>
                      绑定
                    </Button>
                  ) : (
                    <Text2 sx={{ color: 'success.main' }}>已绑定</Text2>
                  )}
                </Stack>
              </Row>
              <Row sx={{ py: 0 }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>账号密码</Text>
                <Text sx={{ width: 590, color: securityProfile?.has_password ? 'success.main' : 'warning.main' }}>
                  {securityProfile?.has_password ? '已设置密码' : '未设置密码'}
                </Text>
                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  <Button
                    size='small'
                    onClick={() => {
                      if (securityProfile?.has_password) {
                        openActionWithGate('change_password', () => setPasswordModalVisible(true))
                      } else {
                        setPasswordModalVisible(true)
                      }
                    }}
                  >
                    {securityProfile?.has_password ? '修改' : '设置'}
                  </Button>
                </Stack>
              </Row>
            </Card>
            <Card>
              <Text>基本信息</Text>
              <Row sx={{ borderBottom: '1px solid #EEEEEE' }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>昵称</Text>
                {editNicknameStatus ? (
                  <TextField
                    variant='outlined'
                    sx={{ width: 590 }}
                    size='small'
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    error={nickname.length > 20}
                    helperText={nickname.length > 20 ? '昵称不能超过20字' : ''}
                  />
                ) : (
                  <Text sx={{ width: 590 }}>{userInfo?.nickname}</Text>
                )}
                <Stack direction='row' sx={{ flex: 1 }} gap={2} justifyContent='flex-end'>
                  {!editNicknameStatus ? (
                    <Button size='small' onClick={openEditNicknameStatus}>
                      修改
                    </Button>
                  ) : (
                    <>
                      <Button size='small' disabled={nickname.trim().length === 0} onClick={onUpdateUserName}>
                        保存
                      </Button>
                      <Button
                        size='small'
                        onClick={() => {
                          setNickname(userInfo?.nickname || '')
                          closeEditNicknameStatus()
                        }}
                      >
                        取消
                      </Button>
                    </>
                  )}
                </Stack>
              </Row>
              <Row sx={{ py: 0 }}>
                <Text sx={{ width: 300, color: 'rgba(0,0,0,0.5)' }}>用户ID</Text>
                <Text sx={{ width: 590 }}>{userInfo?.id || '-'}</Text>
              </Row>
            </Card>
            <Card>
              <Row sx={{ py: 0 }}>
                <Text sx={{ width: 300 }}>实名认证</Text>

                {verified ? (
                  <Stack direction='row' alignItems='center' gap={1} sx={{ color: 'success.main', width: 590 }}>
                    <CheckCircleFilled />
                    已实名
                  </Stack>
                ) : (
                  <Stack direction='row' alignItems='center' gap={1} sx={{ width: 590, color: 'warning.main' }}>
                    <ErrorRoundedIcon sx={{ fontSize: 20 }} />
                    未实名认证
                  </Stack>
                )}

                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  {!verified && (
                    <Button size='small' onClick={() => setRealNameAuthModalVisible(true)}>
                      去认证
                    </Button>
                  )}
                </Stack>
              </Row>
            </Card>
            <Card>
              <Row sx={{ py: 0, alignItems: 'flex-start' }}>
                <Text sx={{ width: 300 }}>
                  账号注销
                  <Text2 sx={{ color: 'rgba(0,0,0,0.5)' }}>永久删除账号和所有数据，请谨慎操作</Text2>
                </Text>
                <Stack direction='row' sx={{ flex: 1 }} justifyContent='flex-end'>
                  <Button size='small' color='error' onClick={() => openActionWithGate('close_account', () => setWriteOffModalVisible(true))}>
                    注销
                  </Button>
                </Stack>
              </Row>
            </Card>
          </>
        )}
        {value === TabValue.Message && <History />}
        {value === TabValue.Promotion && <Promotion />}
      </Stack>
    </Box>
  )
}

export default Personal
